import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  APIActions,
  BlankState,
  Box,
  Button,
  ContentTemplate,
  CustomDialog,
  Grid,
  MUIDataTable,
  MUIDataTableOptions,
  SectionHeader,
  Typography,
} from 'web-library';


import { makeStyles } from 'tss-react/mui';
import { useHistory, useLocation } from 'react-router-dom';
import RouteKey from '../../../../../routes/key';
import {
  useDeleteAccessArrangementRequest,
  useGetAccessArrangementRequest,
} from '../../../../../api/candidate/access-arrangement/@candidate/queries-services/queries';
import { useAccessArrangementsColumns } from '../../../logic/useAccessArrangementRequest';
import { AccessArrangementResultListing, ApplicationStatus } from './AccessArrangementsModels';
import { parseLocationSearch } from '../../../../../utils/StringUtils';

const { accessArrangement } = RouteKey.child;
const { root, importantNotes } = RouteKey['access-arrangement'].child;

const useStyles = makeStyles()((theme) =>
({
  emptyReserve: {
    height: 314,
  },
  emptyContainer: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 77,
    paddingBottom: 77,
  },
  alert: {
    marginBottom: theme.spacing(3),
  },
  lastStatement: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(7),
  },
}));

const AccessArrangements = () => {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState('');

  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();

  const location = useLocation<{ key?: string }>();
  const queryParams = parseLocationSearch(location.search);

  const { data: accessArrangementRequest, isLoading, refetch } = useGetAccessArrangementRequest({
    key: queryParams.key || '',
  });

  const deleteAccessArrangementRequest = useDeleteAccessArrangementRequest(() => refetch());

  const handleApply = () =>
    history.push(`/${accessArrangement}/${root.route}/${importantNotes.route}`);

  const handleBackToHomepage = () => history.push('/');

  const handleOpenDeleteModal = (uuid: string) => {
    setDeleteData(uuid);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDelete = () => {
    if (deleteData) {
      deleteAccessArrangementRequest.mutate(deleteData);
    }

    handleCloseDeleteModal();
  };

  const tableColumns = useAccessArrangementsColumns(
    handleOpenDeleteModal,
    accessArrangementRequest
  );

  const tableOptions: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: (
          <>
            <Box className={classes.emptyReserve}>
              <></>
            </Box>
            <Box className={classes.emptyContainer}>
              {accessArrangementRequest?.accessArrangementOpen ? (
                <BlankState
                  label={t('accessArrangement->>AccessArrangements.Empty')}
                  HelperComponent={
                    <Button
                      id="button-apply-for-access-arrangements"
                      variant="text"
                      onClick={handleApply}
                      color="primary"
                      disabled={accessArrangementRequest?.noExamRegistered}
                    >
                      {t('accessArrangement->>AccessArrangements.Apply')}
                    </Button>
                  }
                />
              ) : (
                <BlankState
                  label={t('accessArrangement->>AccessArrangements.WindowClosed')}
                  HelperComponent={
                    <Button variant="text" onClick={handleBackToHomepage} color="primary">
                      {t('accessArrangement->>AccessArrangements.BackToHomepage')}
                    </Button>
                  }
                />
              )}
            </Box>
          </>
        ),
      },
    },
  };

  const accessArrangementAlert = (accessArrangementResult: AccessArrangementResultListing) => {
    if (accessArrangementResult.accessArrangementOpen) {
      if (accessArrangementResult.noExamRegistered) {
        return (
          <Alert
            className={classes.alert}
            variant="filled"
            severity="error"
            message={t('accessArrangement->>AccessArrangements.Alert')}
          />
        );
      }
    } else if (
      accessArrangementResult.accessArrangementResultModels?.length &&
      accessArrangementResult.accessArrangementResultModels[0].requestStatus ===
      ApplicationStatus.DRAFT
    ) {
      return (
        <Alert
          className={classes.alert}
          variant="filled"
          severity="error"
          message={t('accessArrangement->>AccessArrangements.Unavailable', {
            examLevels: getUnavailableExamLevels(accessArrangementResult),
          })}
        />
      );
    }
    return <></>;
  };

  const getUnavailableExamLevels = (accessArrangementResult: AccessArrangementResultListing) => {
    const unavailableExamLevels: string[] = [];

    accessArrangementResult.accessArrangementResultModels.forEach((accessArrangementItem) => {
      if (
        accessArrangementItem.requestStatus === ApplicationStatus.DRAFT &&
        !accessArrangementResult.accessArrangementOpen
      ) {
        accessArrangementItem.examLevels.split(',').forEach((examLevel) => {
          if (
            unavailableExamLevels.every(
              (unavailableExamLevel) => unavailableExamLevel !== examLevel
            )
          ) {
            unavailableExamLevels.push(examLevel);
          }
        });
      }
    });
    unavailableExamLevels.sort((a, b) => a.localeCompare(b));

    return unavailableExamLevels.join(', ');
  };

  return (
    <>
      {accessArrangementRequest && (
        <ContentTemplate type="full" transparent>
          <Grid>
            <SectionHeader title={t('accessArrangement->>AccessArrangements.SectionHeader')} />
          </Grid>
          {accessArrangementAlert(accessArrangementRequest)}
          <Grid>
            <MUIDataTable
              columns={tableColumns}
              data={accessArrangementRequest.accessArrangementResultModels || []}
              options={tableOptions}
              isLoading={isLoading}
            />
          </Grid>
          <Typography className={classes.lastStatement}>
            {t('accessArrangement->>AccessArrangements.AAListingStatement')}
          </Typography>
          <CustomDialog
            open={openDeleteModal}
            title={t('accessArrangement->>AccessArrangements.Delete Title')}
            message={t('accessArrangement->>AccessArrangements.Delete Message')}
            action={
              <APIActions
                onClose={handleCloseDeleteModal}
                apiCall={handleDelete}
                cancelString={t('accessArrangement->>AccessArrangements.Cancel')}
                apiString={t('accessArrangement->>AccessArrangements.Proceed')}
              />
            }
          />
        </ContentTemplate>
      )}
    </>
  );
};

export default AccessArrangements;
